
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import PropTypes from 'prop-types';
import Spinner from '../../common/Spinner';
import '../../../styles/dashboard/OrgUser/UploadDataManagement.css';
import './records-table-sticky.css';

const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '');

// Utility to format file size
function formatFileSize(bytes) {
  if (bytes === 0 || !bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Utility to format error summary
function formatErrorSummary(errorSummary) {
  if (typeof errorSummary === 'string') {
    try {
      errorSummary = JSON.parse(errorSummary);
    } catch {
      return [];
    }
  }
  if (Array.isArray(errorSummary)) return errorSummary;
  if (errorSummary && typeof errorSummary === 'object' && errorSummary.error) {
    return [{ error: errorSummary.error }];
  }
  return [];
}

// Utility to get gender label
function getGenderLabel(value) {
  const genderMap = {
    '1': 'Female',
    '2': 'Male',
    '3': 'Transgender'
  };
  return genderMap[value] || value;
}

// Utility to get account type label
function getAccountTypeLabel(value) {
  const accountTypeMap = {
    '01': 'AUTO LOAN',
    '02': 'HOUSING LOAN',
    '03': 'PROPERTY LOAN',
    '04': 'LOAN AGAINST SHARES/SECURITIES',
    '05': 'PERSONAL LOAN',
    '06': 'CONSUMER LOAN',
    '07': 'GOLD LOAN',
    '08': 'EDUCATION LOAN',
    '09': 'LOAN TO PROFESSIONAL',
    '10': 'CREDIT CARD',
    '11': 'LEASING',
    '12': 'OVERDRAFT',
    '13': 'TWO-WHEELER LOAN',
    '14': 'NON-FUNDED CREDIT FACILITY',
    '15': 'LOAN AGAINST BANK DEPOSITS',
    '16': 'FLEET CARD',
    '17': 'Commercial Vehicle Loan',
    '18': 'Telco – Wireless',
    '19': 'Telco – Broadband',
    '20': 'Telco – Landline',
    '31': 'Secured Credit Card',
    '32': 'Used Car Loan',
    '33': 'Construction Equipment Loan',
    '34': 'Tractor Loan',
    '35': 'Corporate Credit Card',
    '36': 'Kisan Credit Card',
    '37': 'Loan on Credit Card',
    '38': 'Prime Minister Jaan Dhan Yojana - Overdraft',
    '39': 'Mudra Loans – Shishu / Kishor / Tarun',
    '40': 'Microfinance – Business Loan',
    '41': 'Microfinance – Personal Loan',
    '42': 'Microfinance – Housing Loan',
    '43': 'Microfinance – Others',
    '44': 'Pradhan Mantri Awas Yojana - Credit Link Subsidy Scheme MAY CLSS',
    '50': 'Business Loan - Secured',
    '51': 'BUSINESS LOAN – GENERAL',
    '52': 'BUSINESS LOAN – PRIORITY SECTOR – SMALL BUSINESS',
    '53': 'BUSINESS LOAN – PRIORITY SECTOR – AGRICULTURE',
    '54': 'BUSINESS LOAN – PRIORITY SECTOR – OTHERS',
    '55': 'BUSINESS NON-FUNDED CREDIT FACILITY- GENERAL',
    '56': 'BUSINESS NON-FUNDED CREDIT FACILITY-PRIORITY SECTOR- SMALL BUSINESS',
    '57': 'BUSINESS NON-FUNDED CREDIT FACILITY-PRIORITY SECTOR-AGRICULTURE',
    '58': 'BUSINESS NON-FUNDED CREDIT FACILITY-PRIORITY SECTOR-OTHERS',
    '59': 'BUSINESS LOAN AGAINST BANK DEPOSITS',
    '61': 'Business Loan - Unsecured',
    '00': 'OTHER'
  };
  return accountTypeMap[value] || value;
}

// Utility to get ownership indicator label
function getOwnershipLabel(value) {
  const ownershipMap = {
    '1': 'Individual',
    '2': 'Authorised User',
    '3': 'Guarantor',
    '4': 'Joint'
  };
  return ownershipMap[value] || value;
}

// Human-readable labels for every DB field shown in Complete Record Details
const FIELD_LABELS = {
  consumerName: 'Consumer Name',
  dateOfBirth: 'Date Of Birth',
  gender: 'Gender',
  incomeTaxNumber: 'Pan Number',
  passportNumber: 'Passport Number',
  passportIssueDate: 'Passport Issue Date',
  passportExpiryDate: 'Passport Expiry Date',
  voterIdNumber: 'Voter ID Number',
  drivingLicenseNumber: 'Driving License Number',
  drivingLicenseExpiryDate: 'Driving License Expiry Date',
  drivingLicenseIssueDate: 'Driving License Issue Date',
  rationCardNumber: 'Ration Card Number',
  universalIdNumber: 'Aadhaar number',
  additionalId1: 'Additional ID 1',
  additionalId2: 'Additional ID 2',
  telephoneNumber: 'Telephone Number',
  telephoneResidence: 'Telephone (Residence)',
  telephoneOffice: 'Telephone (Office)',
  telephoneOfficeExt: 'Telephone Office Extension',
  telephoneOther: 'Telephone (Other)',
  telephoneOtherExt: 'Telephone Other Extension',
  emailId1: 'Email ID 1',
  emailId2: 'Email ID 2',
  address1: 'Address 1',
  address1StateCode: 'Address 1 – State Code',
  address1PinCode: 'Address 1 – Pin Code',
  address1Category: 'Address 1 – Category',
  address1ResidenceCode: 'Address 1 – Residence Code',
  address2: 'Address 2',
  address2StateCode: 'Address 2 – State Code',
  address2PinCode: 'Address 2 – Pin Code',
  address2Category: 'Address 2 – Category',
  address2ResidenceCode: 'Address 2 – Residence Code',
  memberCode: 'Member Code',
  memberShortName: 'Member Name',
  accountNumber: 'Account Number',
  accountType: 'Account Type',
  ownershipIndicator: 'Ownership Indicator',
  dateOpened: 'Date Opened / Disbursed',
  dateOfLastPayment: 'Date Of Last Payment',
  dateClosed: 'Date Closed',
  dateReported: 'Date Reported',
  highCredit: 'High Credit / Sanctioned Amount',
  currentBalance: 'Current Balance',
  amountOverdue: 'Amount Overdue',
  daysPastDue: 'Number Of Days Past Due',
  oldMemberCode: 'Old Member Code',
  oldMemberShortName: 'Old Member Short Name',
  oldAccountNumber: 'Old Account Number',
  oldAccountType: 'Old Account Type',
  oldOwnershipIndicator: 'Old Ownership Indicator',
  suitFiledStatus: 'Suit Filed Status',
  writtenOffSettledStatus: 'Written-Off & Settled Status',
  assetClassification: 'Asset Classification',
  collateralValue: 'Value of Collateral',
  collateralType: 'Type of Collateral',
  creditLimit: 'Credit Limit',
  cashLimit: 'Cash Limit',
  rateOfInterest: 'Rate of Interest',
  repaymentTenure: 'Repayment Tenure',
  emiAmount: 'EMI Amount',
  writtenOffAmountTotal: 'Written-Off Amount (Total)',
  writtenOffAmountPrincipal: 'Written-Off Amount (Principal)',
  settlementAmount: 'Settlement Amount',
  paymentFrequency: 'Payment Frequency',
  actualPaymentAmount: 'Actual Payment Amount',
  occupationCode: 'Occupation Code',
  income: 'Income',
  incomeIndicator: 'Net/Gross Income Indicator',
  incomeFrequency: 'Monthly/Annual Income Indicator',
  validationStatus: 'Validation Status',
  validationErrors: 'Validation Errors',
};

// Format coded field values to human-readable labels
function formatFieldValue(key, value) {
  if (value === null || value === undefined || value === '') return '-';
  if (key === 'gender') return getGenderLabel(String(value));
  if (key === 'accountType' || key === 'oldAccountType') return getAccountTypeLabel(String(value));
  if (key === 'ownershipIndicator' || key === 'oldOwnershipIndicator') return getOwnershipLabel(String(value));
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// Show only canonical upload template fields in Complete Record Details.
// Exclude internal validation/meta columns for a predictable fixed field count.
const COMPLETE_RECORD_KEYS = Object.keys(FIELD_LABELS).filter(
  key => key !== 'validationStatus' && key !== 'validationErrors'
);
const COMPLETE_RECORD_FIELD_COUNT = COMPLETE_RECORD_KEYS.length;



export default function UploadDataManagementShared({
  role = 'orguser',
  uploadEndpoint = '/orguser/data-upload/upload',
  historyEndpoint = '/orguser/data-upload/history',
  authHeader,
  orgSelection = false,
  organizations = [],
}) {
  // --- State from OrgUser UploadDataManagement ---
  // Add missing selectedOrgId state for org selection and useEffect dependency
  const [selectedOrgId] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadFilter, setUploadFilter] = useState('all');
  const [uploadHistory, setUploadHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [batchDetails, setBatchDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [viewTab, setViewTab] = useState('overview');
  const [customerRecords, setCustomerRecords] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [recordsCurrentPage, setRecordsCurrentPage] = useState(1);
  const [recordsTotal, setRecordsTotal] = useState(0);
  const [expandedRecordId, setExpandedRecordId] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const recordsPerPage = 50;
  
  // Main tabs state
  const [mainTab, setMainTab] = useState('upload');  // 'upload' or 'history'
  const [historyCurrentPage, setHistoryCurrentPage] = useState(1);
  const historyPerPage = 10;

  // Filter states for upload history
  const [historySearchTerm, setHistorySearchTerm] = useState('');
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [historyStatusFilter, setHistoryStatusFilter] = useState('all');

  // Fetch batch details and show modal
  const handleViewDetails = async (batchId) => {
    setLoadingDetails(true);
    setShowDetailsModal(true);
    setViewTab('overview');
    setCustomerRecords([]);
    setRecordsCurrentPage(1);
    try {
      let endpoint = `/orguser/data-upload/${batchId}`;
      if (role === 'admin') endpoint = `/admin/data-upload/${batchId}`;
      if (role === 'subadmin') endpoint = `/sub-admin/data-upload/${batchId}`;
      if (role === 'superadmin') endpoint = `/superadmin/data-upload/${batchId}`;
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers: authHeader ? authHeader() : {},
      });
      if (response.data.success && response.data.data) {
        setBatchDetails(response.data.data);
        // Fetch records immediately
        await fetchCustomerRecords(batchId, '', 1);
      }
    } catch (error) {
      toast.error('Failed to load batch details');
      setShowDetailsModal(false);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Handle delete batch confirmation
  const handleDeleteClick = (batchId, fileName) => {
    setDeleteTargetId(batchId);
    setShowDeleteModal(true);
  };

  // Handle delete batch
  const handleDeleteBatch = async () => {
    if (!deleteTargetId) return;
    
    setDeleting(true);
    try {
      let endpoint = `/orguser/data-upload/${deleteTargetId}`;
      if (role === 'admin') endpoint = `/admin/data-upload/${deleteTargetId}`;
      if (role === 'subadmin') endpoint = `/sub-admin/data-upload/${deleteTargetId}`;
      if (role === 'superadmin') endpoint = `/superadmin/data-upload/${deleteTargetId}`;
      
      const response = await axios.delete(`${API_BASE_URL}${endpoint}`, {
        headers: authHeader ? authHeader() : {},
      });
      
      if (response.data.success) {
        toast.success('Upload batch deleted successfully');
        // Refresh the upload history
        await fetchUploadHistory();
        setShowDeleteModal(false);
        setDeleteTargetId(null);
      } else {
        toast.error(response.data.message || 'Failed to delete upload batch');
      }
    } catch (error) {
      console.error('Delete batch error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete upload batch');
    } finally {
      setDeleting(false);
    }
  };

  // Fetch customer records for a given page or all batches
  const fetchCustomerRecords = async (batchId, status = '', page = 1, allBatches = false) => {
    setLoadingRecords(true);
    try {
      let endpoint;
      let params = { limit: recordsPerPage, page };
      if (status && status.trim()) params.validationStatus = status.trim();
      
      // Add search parameter if search term is present
      if (customerSearchTerm && customerSearchTerm.trim()) {
        params.search = customerSearchTerm.trim();
        console.log('🔍 Frontend: Adding search param:', params.search);
      }

      // Always fetch records for a single batch
      endpoint = `/orguser/data-upload/${batchId}/records`;
      if (role === 'admin') endpoint = `/admin/data-upload/${batchId}/records`;
      if (role === 'subadmin') endpoint = `/sub-admin/data-upload/${batchId}/records`;
      if (role === 'superadmin') endpoint = `/superadmin/data-upload/${batchId}/records`;

      console.log('🌐 API Request:', endpoint, 'Params:', params);

      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers: authHeader ? authHeader() : {},
        params
      });
      console.log('DEBUG: Full backend response for customer records:', response.data);
      if (response.data.success && response.data.data) {
        const records = response.data.data.records || [];
        setCustomerRecords(records);
        // Log the total and pagination fields
        console.log('DEBUG: response.data.data.total =', response.data.data.total);
        console.log('DEBUG: response.data.data.pagination =', response.data.data.pagination);
        // Use total, fallback to pagination.total, then count, then records.length
        let total = 0;
        if (typeof response.data.data.total === 'number') {
          total = response.data.data.total;
        } else if (response.data.data.pagination && typeof response.data.data.pagination.total === 'number') {
          total = response.data.data.pagination.total;
        } else if (typeof response.data.data.count === 'number') {
          total = response.data.data.count;
        } else {
          total = records.length;
        }
        console.log('DEBUG: total used for setRecordsTotal =', total);
        setRecordsTotal(total);
      } else {
        setCustomerRecords([]);
        setRecordsTotal(0);
      }
    } catch (error) {
      toast.error('Failed to load customer records');
      setCustomerRecords([]);
      setRecordsTotal(0);
    } finally {
      setLoadingRecords(false);
    }
  };


  // --- Handlers ---
  const fetchUploadHistory = async () => {
    setLoading(true);
    try {
      // Remove duplicate /v1 if present
      let endpoint = historyEndpoint;
      if (API_BASE_URL.endsWith('/v1') && historyEndpoint.startsWith('/v1/')) {
        endpoint = historyEndpoint.replace(/^\/v1/, '');
      }
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers: authHeader ? authHeader() : {},
        params: { page: 1, limit: 20 },
      });
      if (response.data.success) {
        const batches = response.data.data.batches.map(batch => ({
          id: batch.id,
          fileName: batch.fileName,
          fileSize: formatFileSize(batch.fileSize),
          recordsCount: batch.totalRecords,
          status: batch.status,
          uploadedBy: batch.uploadedBy?.fullName || 'Unknown',
          organizationName: batch.organization?.organizationName || batch.organizationName || '-',
          uploadDate: batch.createdAt,
          processedRecords: batch.validRecords,
          failedRecords: batch.invalidRecords,
          validationErrors: batch.errorSummary ? formatErrorSummary(batch.errorSummary) : []
        }));
        setUploadHistory(batches);
      }
    } catch (error) {
      console.error('Fetch upload history error:', error, error?.response);
      toast.error('Failed to fetch upload history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUploadHistory();
    // eslint-disable-next-line
  }, [selectedOrgId]);

  // Trigger search when customerSearchTerm changes (with debounce)
  useEffect(() => {
    console.log('🔍 Search useEffect triggered. batchDetails:', batchDetails?.batch?.id, 'searchTerm:', customerSearchTerm);
    
    if (!batchDetails?.batch?.id) {
      console.log('⚠️ No batchDetails.batch.id, skipping search');
      return;
    }
    
    const delaySearch = setTimeout(() => {
      console.log('⏱️ Search debounce completed, fetching records with term:', customerSearchTerm);
      // Reset to first page when search changes
      setRecordsCurrentPage(1);
      fetchCustomerRecords(batchDetails.batch.id, '', 1);
    }, 300); // 300ms debounce for faster response

    return () => {
      console.log('🧹 Cleaning up search timeout');
      clearTimeout(delaySearch);
    };
    // eslint-disable-next-line
  }, [customerSearchTerm, batchDetails?.batch?.id]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      if (!allowedTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls|csv)$/)) {
        toast.error('Please select a valid Excel file (.xlsx, .xls, .csv)');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size should not exceed 10 MB');
        return;
      }
      setSelectedFile(file);
      toast.info(`File selected: ${file.name}`);
    }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/orguser/data-upload/template`, {
        headers: authHeader ? authHeader() : {},
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer-data-template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Sample Excel template downloaded successfully!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // Export all records (valid and invalid) to Excel with color formatting
  const handleExportToExcel = async (batchId, fileName) => {
    try {
      toast.info('Preparing export...');
      let endpoint = `/orguser/data-upload/${batchId}/export`;
      if (role === 'admin') endpoint = `/admin/data-upload/${batchId}/export`;
      if (role === 'subadmin') endpoint = `/sub-admin/data-upload/${batchId}/export`;
      if (role === 'superadmin') endpoint = `/superadmin/data-upload/${batchId}/export`;
      
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers: authHeader ? authHeader() : {},
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${fileName.replace('.xlsx', '')}_export_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Export completed successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data to Excel');
    }
  };

  // Download report for a batch (valid records as CSV)
  const handleDownloadReport = async (batchId, fileName) => {
    try {
      let endpoint = `/orguser/data-upload/${batchId}/records`;
      if (role === 'admin') endpoint = `/admin/data-upload/${batchId}/records`;
      if (role === 'subadmin') endpoint = `/sub-admin/data-upload/${batchId}/records`;
      if (role === 'superadmin') endpoint = `/superadmin/data-upload/${batchId}/records`;
      const response = await axios.get(`${API_BASE_URL}${endpoint}`, {
        headers: authHeader ? authHeader() : {},
        params: {
          validationStatus: 'valid',
          limit: 10000 // Get all valid records
        }
      });
      if (response.data.success) {
        // Convert to CSV and download
        const records = response.data.data.records;
        const csv = convertToCSV(records);
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${fileName.replace(/\.[^/.]+$/, '')}_report.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success('Report downloaded successfully!');
      } else {
        toast.error('Failed to download report');
      }
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  // Helper to convert records to CSV
  function convertToCSV(records) {
    if (!records || !records.length) return '';
    const keys = Object.keys(records[0]);
    const csvRows = [keys.join(',')];
    for (const row of records) {
      csvRows.push(keys.map(k => JSON.stringify(row[k] ?? '')).join(','));
    }
    return csvRows.join('\n');
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    console.log('🚀 Starting upload...');
    setUploading(true);
    setUploadProgress(0);
    console.log('📊 Upload state set to true, progress: 0');
    
    const startTime = Date.now();
    let processingInterval = null;
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const response = await axios.post(
        `${API_BASE_URL}${uploadEndpoint.startsWith('/') ? uploadEndpoint : '/' + uploadEndpoint}`,
        formData,
        {
          headers: {
            ...authHeader ? authHeader() : {},
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              // File upload takes 0-30% of total progress
              const uploadPercent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              const totalProgress = Math.round(uploadPercent * 0.3); // Scale to 30%
              console.log(`📤 File upload: ${uploadPercent}% → Total: ${totalProgress}%`);
              setUploadProgress(totalProgress);
            } else {
              const estimatedProgress = Math.min(20, Math.round(progressEvent.loaded / 10000));
              setUploadProgress(estimatedProgress);
            }
          }
        }
      );
      
      // File uploaded (30%), now simulate server processing (30-95%)
      console.log('📦 File uploaded, processing on server...');
      setUploadProgress(30);
      
      // Simulate server processing progress
      let currentProgress = 30;
      processingInterval = setInterval(() => {
        if (currentProgress < 90) {
          currentProgress += 3;
          console.log(`⚙️ Processing data: ${currentProgress}%`);
          setUploadProgress(currentProgress);
        }
      }, 100); // Update every 100ms
      
      // Wait a bit to show processing (adjust based on typical processing time)
      await new Promise(resolve => setTimeout(resolve, 800));
      
      if (response.data.success) {
        clearInterval(processingInterval);
        setUploadProgress(95);
        console.log('✅ Data processed, finalizing...');
        
        const result = response.data.data;
        
        // Show completion
        setUploadProgress(100);
        await new Promise(resolve => setTimeout(resolve, 500)); // Show 100% briefly
        
        // Check for warnings
        if (response.data.warning) {
          toast.warning(response.data.warning, { autoClose: 8000 });
        }
        
        // Show summary based on record counts
        if (result.totalRecords === 0) {
          toast.error('No data rows found in Excel file. Please check the file and try again.');
        } else if (result.validRecords === 0 && result.invalidRecords > 0) {
          toast.error(
            `All ${result.invalidRecords} records failed validation. Please fix the errors and upload again.`,
            { autoClose: 8000 }
          );
        } else {
          const processed = result.processedRecords ?? result.totalRecords;
          const updated = result.updatedRecords ?? 0;
          const created = result.createdRecords ?? 0;
          toast.success(
            `File uploaded successfully! Processed: ${processed}, Updated: ${updated}, Created: ${created}, Valid: ${result.validRecords}, Invalid: ${result.invalidRecords}.`,
            { autoClose: 5000 }
          );
          if (result.invalidRecords > 0) {
            setTimeout(() => {
              toast.warning(
                `⚠️ ${result.invalidRecords} ${result.invalidRecords === 1 ? 'entry has' : 'entries have'} validation issues out of ${result.totalRecords} total entries. Check the "Validation Errors" tab for details.`,
                { autoClose: 8000, style: { fontSize: '15px', fontWeight: '600' } }
              );
            }, 1000);
          }
        }
        
        fetchUploadHistory();
        setSelectedFile(null);
        document.getElementById('fileInput').value = '';
      }
    } catch (error) {
      if (processingInterval) clearInterval(processingInterval);
      const errorMsg = error.response?.data?.message || 'Failed to upload file';
      if (errorMsg.includes('Invalid Excel template')) {
        toast.error(errorMsg, { autoClose: 10000 });
        toast.info('Please download the correct template and ensure all column headers match exactly.', { autoClose: 8000 });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      if (processingInterval) clearInterval(processingInterval);
      console.log('✅ Upload completed, resetting state');
      
      // Brief delay to show final state
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setUploading(false);
      setUploadProgress(0);
    }
  };



  // --- Handlers and logic from OrgUser UploadDataManagement ---
  // (Copy all handler functions: fetchUploadHistory, handleFileSelect, handleDownloadSample, handleUpload, handleViewDetails, fetchCustomerRecords, handleDownloadReport, convertToCSV, etc.)
  // For brevity, you can copy these from the original file as needed.

  // --- Derived data ---
  const filteredHistory = uploadHistory.filter(upload => {
    // Search term filter
    if (historySearchTerm) {
      const searchLower = historySearchTerm.toLowerCase();
      const matchesSearch = 
        upload.fileName?.toLowerCase().includes(searchLower) ||
        upload.uploadedBy?.toLowerCase().includes(searchLower) ||
        upload.id?.toString().includes(searchLower);
      if (!matchesSearch) return false;
    }

    // Date range filter
    if (historyDateFrom || historyDateTo) {
      const uploadDate = new Date(upload.uploadDate);
      if (historyDateFrom) {
        const fromDate = new Date(historyDateFrom);
        if (uploadDate < fromDate) return false;
      }
      if (historyDateTo) {
        const toDate = new Date(historyDateTo);
        toDate.setHours(23, 59, 59, 999); // Include the entire end date
        if (uploadDate > toDate) return false;
      }
    }

    // Status filter (combining old filter and new filter)
    const statusToCheck = historyStatusFilter !== 'all' ? historyStatusFilter : uploadFilter;
    if (statusToCheck === 'all') return true;
    return upload.status.toLowerCase() === statusToCheck.toLowerCase();
  });

  // --- UI ---
  return (
    <div className="upload-data-page">
      {/* Progress Spinner during upload */}
      {console.log('🔍 Render - uploading:', uploading, 'progress:', uploadProgress)}
      {uploading && (
        <Spinner
          variant="progress"
          progress={uploadProgress}
          size="large"
          color="primary"
          text={
            uploadProgress < 30 
              ? "Uploading file to server..." 
              : uploadProgress < 90 
              ? "Processing and validating data..." 
              : "Finalizing upload..."
          }
          fullScreen={true}
        />
      )}
      
      <div className="page-header">
        <div>
          <h1>Upload Data Management</h1>
          <p>Bulk upload credit verification data using Excel files</p>
        </div>
      </div>

      {/* Main Tabs - Only show for OrgUser with 2 tabs */}
      {role === 'orguser' && (
        <div className="upload-main-tabs">
          <button 
            className={`main-tab-btn ${mainTab === 'upload' ? 'active' : ''}`}
            onClick={() => setMainTab('upload')}
          >
            📤 Data Upload Management
          </button>
          <button 
            className={`main-tab-btn ${mainTab === 'history' ? 'active' : ''}`}
            onClick={() => setMainTab('history')}
          >
            📋 Upload History
          </button>
        </div>
      )}

      {/* For non-orguser, show single tab or just upload history */}
      {role !== 'orguser' && (
        <div className="upload-main-tabs">
          <button 
            className="main-tab-btn active"
            disabled
          >
            📋 Upload History
          </button>
        </div>
      )}

      {/* Tab 1: Data Upload Management - Only for OrgUser */}
      {role === 'orguser' && mainTab === 'upload' && (
        <>
          {/* Upload Section: Only show for orguser */}
          <div className="upload-section-card">
            <div className="upload-header">
              <h2>📤 Upload New Data</h2>
              <button className="btn-download-sample" onClick={handleDownloadSample}>
                📥 Download Sample Excel
              </button>
            </div>

            <div className="upload-area">
              <div className="upload-instructions">
                <h3>Instructions for Upload:</h3>
                <ul>
                  <li>Download the sample Excel template to see the required format</li>
                  <li>Fill in customer details: Name, PAN, Aadhaar, Contact, etc.</li>
                  <li>Ensure all mandatory fields are filled correctly</li>
                  <li>File size should not exceed 10 MB</li>
                  <li>Supported format: .xlsx, .xls, .csv</li>
                </ul>
              </div>

              <div className="file-upload-container">
                <div className="file-drop-zone">
                  <input
                    type="file"
                    id="fileInput"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="file-input-hidden"
                  />
                  <label htmlFor="fileInput" className="file-drop-label">
                    <div className="upload-icon">📁</div>
                    <h3>Click to browse or drag and drop</h3>
                    <p>Excel files only (.xlsx, .xls, .csv) • Max 10 MB</p>
                  </label>
                </div>

                {selectedFile && (
                  <div className="selected-file-info">
                    <div className="file-icon">📄</div>
                    <div className="file-details">
                      <h4>{selectedFile.name}</h4>
                      <p>{(selectedFile.size / 1024).toFixed(2)} KB</p>
                    </div>
                    <button
                      className="remove-file-btn"
                      onClick={() => {
                        setSelectedFile(null);
                        document.getElementById('fileInput').value = '';
                      }}
                    >
                      ❌
                    </button>
                  </div>
                )}
                <button
                  className={`btn-upload ${selectedFile ? 'active' : ''}`}
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                  style={selectedFile ? { backgroundColor: '#16a34a', color: 'white' } : {}}
                >
                  {uploading ? 'Uploading...' : selectedFile ? 'Submit' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Tab 2: Upload History - For OrgUser and other roles */}
      {((role === 'orguser' && mainTab === 'history') || role !== 'orguser') && (
        <div className="history-section">
          <div className="history-header">
            <h2>📋 Upload History</h2>
          </div>

          {/* Search and Filter Row */}
          <div className="history-search-filter-row">
            {/* Search Bar */}
            <div className="history-search-wrapper">
              <span className="history-search-icon">🔍</span>
              <input
                type="text"
                placeholder="Search by file name, uploaded by, batch ID..."
                value={historySearchTerm}
                onChange={(e) => setHistorySearchTerm(e.target.value)}
                className="history-search-input"
              />
            </div>

            {/* Filters Group */}
            <div className="history-filters-group">
              {/* Date Range Filter */}
              <div className="history-date-filter">
                <input
                  type="date"
                  value={historyDateFrom}
                  onChange={(e) => setHistoryDateFrom(e.target.value)}
                  className="history-date-input"
                />
                <span className="history-date-separator">to</span>
                <input
                  type="date"
                  value={historyDateTo}
                  onChange={(e) => setHistoryDateTo(e.target.value)}
                  className="history-date-input"
                />
              </div>

              {/* Status Filter */}
              <select
                value={historyStatusFilter}
                onChange={(e) => setHistoryStatusFilter(e.target.value)}
                className="history-status-select"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="processing">Processing</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>

          {/* Old Filter Tabs - Keeping for backward compatibility */}
          <div className="filter-tabs" style={{ marginBottom: '1rem' }}>
            <button
              className={`filter-tab ${uploadFilter === 'all' ? 'active' : ''}`}
              onClick={() => { setUploadFilter('all'); setHistoryStatusFilter('all'); setHistoryCurrentPage(1); }}
            >
              All
            </button>
            <button
              className={`filter-tab ${uploadFilter === 'completed' ? 'active' : ''}`}
              onClick={() => { setUploadFilter('completed'); setHistoryStatusFilter('completed'); setHistoryCurrentPage(1); }}
            >
              Completed
            </button>
            <button
              className={`filter-tab ${uploadFilter === 'processing' ? 'active' : ''}`}
              onClick={() => { setUploadFilter('processing'); setHistoryStatusFilter('processing'); setHistoryCurrentPage(1); }}
            >
              Processing
            </button>
            <button
              className={`filter-tab ${uploadFilter === 'failed' ? 'active' : ''}`}
              onClick={() => { setUploadFilter('failed'); setHistoryStatusFilter('failed'); setHistoryCurrentPage(1); }}
            >
              Failed
            </button>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading upload history...</p>
            </div>
          ) : (
            <>
              <div className="history-table-wrapper">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>File Name</th>
                      <th>Organization</th>
                      <th>Status</th>
                      <th>Total Records</th>
                      <th>Valid Records</th>
                      <th>Invalid Records</th>
                      <th>File Size</th>
                      <th>Uploaded By</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredHistory.slice((historyCurrentPage - 1) * historyPerPage, historyCurrentPage * historyPerPage).map((upload, index) => (
                      <tr key={upload.id} className="history-row">
                        <td className="text-center">{(historyCurrentPage - 1) * historyPerPage + index + 1}</td>
                        <td className="file-name-cell">
                          <span className="file-icon">📊</span>
                          <div className="file-info">
                            <p>{upload.fileName}</p>
                          </div>
                        </td>
                        <td className="org-name-cell">
                          {upload.organizationName}
                        </td>
                        <td>
                          <span className={`status-badge ${upload.status}`}>
                            {upload.status}
                          </span>
                        </td>
                        <td className="text-center">{upload.recordsCount}</td>
                        <td className="text-center">
                          <span className="valid-records-badge">{upload.processedRecords}</span>
                        </td>
                        <td className="text-center">
                          <span className={`invalid-records-badge ${upload.failedRecords > 0 ? 'has-errors' : ''}`}>
                            {upload.failedRecords}
                          </span>
                        </td>
                        <td className="text-center">{upload.fileSize}</td>
                        <td>{upload.uploadedBy}</td>
                        <td>{new Date(upload.uploadDate).toLocaleString('en-IN')}</td>
                        <td className="actions-cell">
                          <button
                            className="btn-table-action"
                            onClick={() => handleViewDetails(upload.id)}
                            title="View Details"
                          >
                            👁️
                          </button>
                          {role === 'superadmin' && (
                            <button
                              className="btn-table-action btn-delete"
                              onClick={() => handleDeleteClick(upload.id, upload.fileName)}
                              title="Delete Upload Batch"
                              style={{ marginLeft: '8px', color: '#f44336' }}
                            >
                              🗑️
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Total Files Count */}
              {filteredHistory.length > 0 && (
                <div style={{
                  padding: '16px',
                  background: '#f5f5f5',
                  borderRadius: '8px',
                  marginTop: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontWeight: '600',
                  color: '#333'
                }}>
                  <span style={{ fontSize: '24px' }}>📁</span>
                  <span>Total Files Uploaded: <span style={{ color: '#4CAF50', fontSize: '18px' }}>{filteredHistory.length}</span></span>
                </div>
              )}

              {/* Pagination for History */}
              {filteredHistory.length > historyPerPage && (
                <div className="history-pagination-wrapper">
                  <div className="pagination-info">
                    Showing {((historyCurrentPage - 1) * historyPerPage) + 1} to {Math.min(historyCurrentPage * historyPerPage, filteredHistory.length)} of {filteredHistory.length} records
                  </div>
                  <div className="history-pagination">
                    <button 
                      disabled={historyCurrentPage === 1}
                      onClick={() => setHistoryCurrentPage(prev => Math.max(1, prev - 1))}
                    >
                      Previous
                    </button>
                    {(() => {
                      const totalPages = Math.ceil(filteredHistory.length / historyPerPage);
                      const buttons = [];
                      const maxVisible = 7;
                      let startPage = Math.max(1, historyCurrentPage - Math.floor(maxVisible / 2));
                      let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                      if (endPage - startPage < maxVisible - 1) {
                        startPage = Math.max(1, endPage - maxVisible + 1);
                      }

                      if (startPage > 1) {
                        buttons.push(
                          <button key={1} onClick={() => setHistoryCurrentPage(1)} className={historyCurrentPage === 1 ? 'active' : ''}>
                            1
                          </button>
                        );
                        if (startPage > 2) buttons.push(<span key="start-ellipsis">...</span>);
                      }

                      for (let i = startPage; i <= endPage; i++) {
                        buttons.push(
                          <button 
                            key={i} 
                            onClick={() => setHistoryCurrentPage(i)}
                            className={historyCurrentPage === i ? 'active' : ''}
                          >
                            {i}
                          </button>
                        );
                      }

                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) buttons.push(<span key="end-ellipsis">...</span>);
                        buttons.push(
                          <button key={totalPages} onClick={() => setHistoryCurrentPage(totalPages)} className={historyCurrentPage === totalPages ? 'active' : ''}>
                            {totalPages}
                          </button>
                        );
                      }

                      return buttons;
                    })()}
                    <button 
                      disabled={historyCurrentPage === Math.ceil(filteredHistory.length / historyPerPage)}
                      onClick={() => setHistoryCurrentPage(prev => prev + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}

              {!loading && filteredHistory.length === 0 && (
                <div className="empty-state">
                  <div className="empty-state-icon">📭</div>
                  <h3>No upload history found</h3>
                  <p>Upload your first Excel file to get started</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && (
        <div className="modal-overlay upload-data-modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📋 Upload Batch Details</h2>
              <div className="modal-header-actions">
                <button 
                  className="btn-export-excel"
                  onClick={() => handleExportToExcel(batchDetails.batch.id, batchDetails.batch.fileName)}
                  title="Export all records to Excel with invalid records highlighted"
                >
                  📊 Export to Excel
                </button>
                <button className="modal-close" onClick={() => setShowDetailsModal(false)}>
                  ✕
                </button>
              </div>
            </div>
            {loadingDetails ? (
              <div className="modal-loading">
                <div className="spinner"></div>
                <p>Loading details...</p>
              </div>
            ) : batchDetails ? (
              <div className="modal-body">
                {/* Tabs */}
                <div className="detail-tabs">
                  <button 
                    className={`tab-btn ${viewTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setViewTab('overview')}
                  >
                    📊 Overview
                  </button>
                  <button 
                    className={`tab-btn ${viewTab === 'validation' ? 'active' : ''}`}
                    onClick={() => setViewTab('validation')}
                  >
                    ⚠️ Validation Errors ({batchDetails.batch.invalidRecords})
                  </button>
                  <button 
                    className={`tab-btn ${viewTab === 'all' ? 'active' : ''}`}
                    onClick={() => {
                      setViewTab('all');
                      if (batchDetails?.batch?.id) {
                        fetchCustomerRecords(batchDetails.batch.id, '', 1);
                      }
                    }}
                  >
                    📋 All Records ({recordsTotal || batchDetails.batch.totalRecords})
                  </button>
                </div>

                {/* Overview Tab */}
                {viewTab === 'overview' && (
                  <>
                    <div className="batch-info-section">
                      <h3>Batch Information</h3>
                      <div className="info-grid">
                        <div className="info-item">
                          <strong>File Name:</strong> {batchDetails.batch.fileName}
                        </div>
                        <div className="info-item">
                          <strong>File Size:</strong> {formatFileSize(batchDetails.batch.fileSize)}
                        </div>
                        <div className="info-item">
                          <strong>Status:</strong> 
                          <span className={`status-badge ${batchDetails.batch.status}`}>
                            {batchDetails.batch.status}
                          </span>
                        </div>
                        <div className="info-item">
                          <strong>Uploaded By:</strong> {batchDetails.batch.uploadedBy?.fullName || 'Unknown'}
                        </div>
                        <div className="info-item">
                          <strong>Upload Date:</strong> {new Date(batchDetails.batch.createdAt).toLocaleString('en-IN')}
                        </div>
                      </div>
                    </div>

                    <div className="batch-stats-section">
                      <h3>Processing Statistics</h3>
                      <div className="stats-row">
                        <div className="stat-box">
                          <div className="stat-number">{batchDetails.batch.processedRecords || batchDetails.batch.totalRecords}</div>
                          <div className="stat-label">Rows Uploaded</div>
                        </div>
                        <div className="stat-box">
                          <div className="stat-number">{batchDetails.batch.totalRecords}</div>
                          <div className="stat-label">Current Records</div>
                        </div>
                        <div className="stat-box success">
                          <div className="stat-number">{batchDetails.batch.validRecords}</div>
                          <div className="stat-label">Valid Records</div>
                        </div>
                        <div className="stat-box error">
                          <div className="stat-number">{batchDetails.batch.invalidRecords}</div>
                          <div className="stat-label">Invalid Records</div>
                        </div>
                      </div>
                    </div>

                    {/* No Records Alert */}
                    {batchDetails.batch.totalRecords === 0 && (
                      <div className="alert-box alert-warning" style={{ marginTop: '20px', padding: '16px', borderLeft: '4px solid #ff9800' }}>
                        <h4 style={{ marginTop: 0, color: '#e65100' }}>⚠️ Empty Batch</h4>
                        <p style={{ marginBottom: 0 }}>
                          This batch has no records. This typically happens if the uploaded Excel file had no data rows or if all rows contained data processing errors. 
                          Please re-upload the file with correct data.
                        </p>
                      </div>
                    )}
                      <div className="invalid-records-section">
                        <h3>⚠️ Validation Error Summary</h3>
                        <div className="error-summary-card">
                          <div className="error-summary-content">
                            <div className="error-summary-main">
                              <span className="error-count-large">{batchDetails.batch.invalidRecords}</span>
                              <span className="error-text-main">
                                {batchDetails.batch.invalidRecords === 1 ? 'entry' : 'entries'} having issue
                              </span>
                            </div>
                            <div className="error-summary-divider"></div>
                            <div className="error-summary-total">
                              <span className="error-total-label">out of</span>
                              <span className="error-total-count">{batchDetails.batch.totalRecords}</span>
                              <span className="error-total-label">total entries</span>
                            </div>
                          </div>
                        </div>
                        <p className="more-records-note">
                          Click the <strong>"⚠️ Validation Errors"</strong> tab above to view detailed error information for each record
                        </p>
                      </div>
                    )}

                    {/* No Errors Message */}
                    {batchDetails.batch.invalidRecords === 0 && (
                      <div className="success-message">
                        <div className="success-icon">✅</div>
                        <h3>All Records Valid!</h3>
                        <p>All {batchDetails.batch.totalRecords} records passed validation successfully.</p>
                      </div>
                    )}
                  </>
                )}

                {/* Validation Errors Tab */}
                {viewTab === 'validation' && (
                  <div className="validation-errors-view">
                    {batchDetails.batch.invalidRecords === 0 ? (
                      <div className="success-message">
                        <div className="success-icon">✅</div>
                        <h3>No Validation Errors!</h3>
                        <p>All {batchDetails.batch.totalRecords} records passed validation successfully.</p>
                      </div>
                    ) : (
                      <>
                        <div className="validation-summary-header">
                          <h3>⚠️ Validation Error Summary</h3>
                          <div className="error-summary-card">
                            <div className="error-summary-content">
                              <div className="error-summary-main">
                                <span className="error-count-large">{batchDetails.batch.invalidRecords}</span>
                                <span className="error-text-main">
                                  {batchDetails.batch.invalidRecords === 1 ? 'entry' : 'entries'} having issue
                                </span>
                              </div>
                              <div className="error-summary-divider"></div>
                              <div className="error-summary-total">
                                <span className="error-total-label">out of</span>
                                <span className="error-total-count">{batchDetails.batch.totalRecords}</span>
                                <span className="error-total-label">total entries</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="detailed-errors-section">
                          <h4>View Detailed Error Information:</h4>
                          <button 
                            className="btn-view-details"
                            onClick={() => {
                              setViewTab('all');
                              fetchCustomerRecords(batchDetails.batch.id, 'invalid');
                            }}
                          >
                            📋 View All Invalid Records
                          </button>
                          <p className="error-details-note">
                            Click the button above to see detailed validation error messages for each record
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* All Records Tab with Pagination and backend fetch */}

                {viewTab === 'all' && (
                  <div className="records-view">
                    {batchDetails?.batch?.processedRecords > recordsTotal && (
                      <div className="alert-box alert-warning" style={{ marginBottom: '16px', padding: '10px 14px' }}>
                        <strong>Note:</strong> Uploaded rows: {batchDetails.batch.processedRecords}. Current records in this batch: {recordsTotal}. Duplicate rows are merged by unique keys during processing.
                      </div>
                    )}
                    {/* Search Bar for Customer Records */}
                    <div className="search-bar-container" style={{ marginBottom: '20px', display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="text"
                        placeholder="🔍 Search by Customer Name, PAN, or Aadhaar..."
                        value={customerSearchTerm}
                        onChange={(e) => setCustomerSearchTerm(e.target.value)}
                        className="search-input"
                        style={{
                          flex: 1,
                          padding: '12px 16px',
                          fontSize: '14px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          outline: 'none',
                          transition: 'border-color 0.3s'
                        }}
                        onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                      />
                      {customerSearchTerm && (
                        <button
                          onClick={() => setCustomerSearchTerm('')}
                          style={{
                            padding: '12px 20px',
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {/* Table container with horizontal scroll only for table */}
                    <div className="records-table-container">
                      <table className="records-table">
                        <thead>
                          <tr>
                            <th>Sr No</th>
                            <th className="col-expand"></th>
                            <th>Status</th>
                            <th><span className="mandatory-field">Consumer Name</span></th>
                            <th><span className="mandatory-field">Date Of Birth</span></th>
                            <th title="Valid values: 1=Female, 2=Male, 3=Transgender"><span className="mandatory-field">Gender</span></th>
                            <th><span className="mandatory-field">Pan Number</span></th>
                            <th><span className="mandatory-field">Aadhaar number</span></th>
                            <th><span className="mandatory-field">Address</span></th>
                            <th><span className="mandatory-field">Pin Code</span></th>
                            <th><span className="mandatory-field">Member Code</span></th>
                            <th><span className="mandatory-field">Member Name</span><br/><span className="field-note">(Match organization name)</span></th>
                            <th><span className="mandatory-field">Account Number</span></th>
                            <th title="Valid values: 01-61, 00. Hover over cells to see account type names."><span className="mandatory-field">Account Type</span></th>
                            <th title="Valid values: 1=Individual, 2=Authorised User, 3=Guarantor, 4=Joint"><span className="mandatory-field">Ownership Indicator</span></th>
                            <th><span className="mandatory-field">Date Opened</span></th>
                            <th><span className="mandatory-field">Date Reported</span></th>
                            <th><span className="mandatory-field">High Credit</span></th>
                            <th><span className="mandatory-field">Current Balance</span></th>
                            <th><span className="mandatory-field">Amount Overdue</span></th>
                            <th><span className="mandatory-field">Days Past Due</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          {customerRecords.map((record, index) => (
                            <React.Fragment key={record.id}>
                                <tr
                                  className={`record-row ${record.validationStatus}`}
                                  title={
                                    record.validationStatus === 'invalid' && record.validationErrors
                                      ? JSON.parse(record.validationErrors || '[]').join('\n')
                                      : undefined
                                  }
                                >
                                <td>{(recordsCurrentPage - 1) * recordsPerPage + index + 1}</td>
                                <td className="col-expand">
                                  <button 
                                    className="expand-btn"
                                    onClick={() => setExpandedRecordId(expandedRecordId === record.id ? null : record.id)}
                                    title="Click to view all fields"
                                  >
                                    {expandedRecordId === record.id ? '▼' : '▶'}
                                  </button>
                                </td>
                                <td>
                                  <span className={`status-badge ${record.validationStatus}`}>
                                    {record.validationStatus === 'valid' ? '✅ Valid' : '⚠️ Invalid'}
                                  </span>
                                </td>
                                <td>{record.consumerName || '-'}</td>
                                <td>{record.dateOfBirth || '-'}</td>
                                <td title={record.gender ? getGenderLabel(record.gender) : undefined}>
                                  {record.gender || '-'}
                                </td>
                                <td>{record.incomeTaxNumber || '-'}</td>
                                <td>{record.universalIdNumber || '-'}</td>
                                <td>{record.address1 || '-'}</td>
                                <td>{record.address1PinCode || '-'}</td>
                                <td>{record.memberCode || '-'}</td>
                                <td>{record.memberShortName || '-'}</td>
                                <td>{record.accountNumber || '-'}</td>
                                <td title={record.accountType ? getAccountTypeLabel(record.accountType) : undefined}>
                                  {record.accountType || '-'}
                                </td>
                                <td title={record.ownershipIndicator ? getOwnershipLabel(record.ownershipIndicator) : undefined}>
                                  {record.ownershipIndicator || '-'}
                                </td>
                                <td>{record.dateOpened || '-'}</td>
                                <td>{record.dateReported || '-'}</td>
                                <td>{record.highCredit || '-'}</td>
                                <td>{record.currentBalance || '-'}</td>
                                <td>{record.amountOverdue || '-'}</td>
                                <td>{record.daysPastDue || '-'}</td>
                              </tr>
                              {/* Expanded Row - All Fields */}
                              {expandedRecordId === record.id && (
                                <tr className="expanded-row">
                                  <td colSpan="18">
                                    <div className="record-details-grid">
                                      <h4>📋 Complete Record Details ({COMPLETE_RECORD_FIELD_COUNT} fields)</h4>
                                      <div className="details-fields">
                                        {/* Show fixed canonical upload fields using a static key list.
                                            Using a fixed key list (not Object.entries) guarantees
                                            every field is rendered regardless of whether the API
                                            returns the key (null/empty DB values may be omitted
                                            by Sequelize toJSON). */}
                                        {COMPLETE_RECORD_KEYS
                                          .map(key => {
                                            const value = record[key];
                                            const displayValue = formatFieldValue(key, value);
                                            return (
                                              <div key={key} className="detail-field">
                                                <label>{FIELD_LABELS[key]}:</label>
                                                <span title={String(displayValue)}>{displayValue}</span>
                                              </div>
                                            );
                                          })}
                                      </div>
                                      {record.validationStatus === 'invalid' && record.validationErrors && (
                                        <div className="record-errors-detail">
                                          <h5>❌ Validation Errors:</h5>
                                          <ul>
                                            {JSON.parse(record.validationErrors || '[]').map((error, i) => (
                                              <li key={i}>{error}</li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Sticky Pagination Bottom */}
                    {recordsTotal > recordsPerPage && (
                      <div className="records-pagination records-pagination-sticky-bottom sticky-pagination records-pagination-end">
                        <button
                          disabled={recordsCurrentPage === 1}
                          onClick={() => {
                            setRecordsCurrentPage(recordsCurrentPage - 1);
                            fetchCustomerRecords(batchDetails.batch.id, '', recordsCurrentPage - 1);
                          }}
                        >
                          Previous
                        </button>
                        {(() => {
                          const totalPages = Math.ceil(recordsTotal / recordsPerPage);
                          const pageButtons = [];
                          if (totalPages <= 7) {
                            for (let i = 1; i <= totalPages; i++) {
                              pageButtons.push(
                                <button
                                  key={i}
                                  className={recordsCurrentPage === i ? 'active' : ''}
                                  onClick={() => {
                                    setRecordsCurrentPage(i);
                                    fetchCustomerRecords(batchDetails.batch.id, '', i);
                                  }}
                                >
                                  {i}
                                </button>
                              );
                            }
                          } else {
                            // Always show first, last, current, and neighbors
                            if (recordsCurrentPage > 3) {
                              pageButtons.push(
                                <button key={1} className={recordsCurrentPage === 1 ? 'active' : ''} onClick={() => { setRecordsCurrentPage(1); fetchCustomerRecords(batchDetails.batch.id, '', 1); }}>1</button>
                              );
                              if (recordsCurrentPage > 4) pageButtons.push(<span key="start-ellipsis">...</span>);
                            }
                            for (let i = Math.max(2, recordsCurrentPage - 1); i <= Math.min(totalPages - 1, recordsCurrentPage + 1); i++) {
                              pageButtons.push(
                                <button
                                  key={i}
                                  className={recordsCurrentPage === i ? 'active' : ''}
                                  onClick={() => {
                                    setRecordsCurrentPage(i);
                                    fetchCustomerRecords(batchDetails.batch.id, '', i);
                                  }}
                                >
                                  {i}
                                </button>
                              );
                            }
                            if (recordsCurrentPage < totalPages - 2) {
                              if (recordsCurrentPage < totalPages - 3) pageButtons.push(<span key="end-ellipsis">...</span>);
                              pageButtons.push(
                                <button key={totalPages} className={recordsCurrentPage === totalPages ? 'active' : ''} onClick={() => { setRecordsCurrentPage(totalPages); fetchCustomerRecords(batchDetails.batch.id, '', totalPages); }}>{totalPages}</button>
                              );
                            }
                          }
                          return pageButtons;
                        })()}
                        <button
                          disabled={recordsCurrentPage === Math.ceil(recordsTotal / recordsPerPage)}
                          onClick={() => {
                            setRecordsCurrentPage(recordsCurrentPage + 1);
                            fetchCustomerRecords(batchDetails.batch.id, '', recordsCurrentPage + 1);
                          }}
                        >
                          Next
                        </button>
                      </div>
                    )}

                    {/* Empty state */}
                    {!loadingRecords && customerRecords.length === 0 && (
                      <div className="empty-state">
                        {customerSearchTerm.trim() ? (
                          <>
                            <div className="empty-state-icon">🔍</div>
                            <h3>No matching records found</h3>
                            <p>Try adjusting your search term</p>
                          </>
                        ) : (
                          <p>No records found</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="modal-error">
                <p>Failed to load batch details</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay upload-data-modal-overlay" onClick={() => !deleting && setShowDeleteModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>⚠️ Confirm Delete</h2>
              <button className="modal-close" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>🗑️</div>
                <h3 style={{ marginBottom: '16px', color: '#333' }}>Delete Upload Batch?</h3>
                <p style={{ color: '#666', marginBottom: '24px', lineHeight: '1.6' }}>
                  Are you sure you want to delete this upload batch? This will permanently remove:
                </p>
                <ul style={{ textAlign: 'left', color: '#666', marginBottom: '24px', paddingLeft: '40px' }}>
                  <li>All customer records in this batch</li>
                  <li>Batch information and statistics</li>
                  <li>Upload history entry</li>
                </ul>
                <p style={{ color: '#d32f2f', fontWeight: 'bold', marginBottom: '24px' }}>
                  ⚠️ This action cannot be undone!
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleting}
                    style={{
                      padding: '12px 24px',
                      background: '#e0e0e0',
                      color: '#333',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      opacity: deleting ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteBatch}
                    disabled={deleting}
                    style={{
                      padding: '12px 24px',
                      background: deleting ? '#ccc' : '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: deleting ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {deleting ? (
                      <>
                        <div className="spinner" style={{ width: '16px', height: '16px', borderWidth: '2px' }}></div>
                        Deleting...
                      </>
                    ) : (
                      <>🗑️ Delete Permanently</>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

UploadDataManagementShared.propTypes = {
  role: PropTypes.string,
  uploadEndpoint: PropTypes.string,
  historyEndpoint: PropTypes.string,
  authHeader: PropTypes.func,
  orgSelection: PropTypes.bool,
  organizations: PropTypes.array,
};
